import {
  Box,
  Button,
  Dialog,
  Heading,
  Icon,
  Label,
  Select,
  Text,
  useToast,
  TransparentButton,
  LoadingSpinner,
} from '@redriverpress/system'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useQuery } from 'react-query'

import {
  getTeacherClasses,
  addLessonToClass,
  getClassesContainingContent,
} from 'Common/services/planner'
import CLASS_VERSION from 'Common/constants/classVersion'

import {
  CONTENT_CLASS_VERSIONS,
  CONTENT_TYPES,
  COURSE_TYPES,
} from '../../../constants/contentTypes'
import trackAddContentToClass from './trackAddContentToClass'
import { SYLLABUS_SECTIONS } from '../../../../../teacher/planner/common/constants'

const PLANNER_SECTIONS = {
  saved: 0,
  prepping: 1,
  teaching: 2,
  taught: 3,
}
const CONTENT_ID_KEY = {
  [CONTENT_TYPES.flashcardSet]: 'flashcard_set_id',
  [CONTENT_TYPES.flashcardCategory]: 'flashcard_category_id',
  [CONTENT_TYPES.video]: 'video_id',
  [CONTENT_TYPES.course]: 'course_id',
  [CONTENT_TYPES.lesson]: 'lesson_id',
}

const ALLOWS_DEFAULT_ASSIGNMENT = {
  [CONTENT_TYPES.lesson]: true,
  [CONTENT_TYPES.video]: true,
  [CONTENT_TYPES.flashcardSet]: false,
  [CONTENT_TYPES.flashcardCategory]: false,
  [CONTENT_TYPES.course]: true,
}

const COURSE_TYPE_WARNING = {
  [COURSE_TYPES.course]:
    'Only the first item will be visible to students by default.',
}

const SYLLABUS_SECTIONS_LIST = [
  SYLLABUS_SECTIONS.saved,
  SYLLABUS_SECTIONS.prepping,
  SYLLABUS_SECTIONS.teaching,
]

export default function AddToClassDialog({
  content,
  contentType,
  courseType,
  onCancel,
  onError,
  onSave,
  onOpenChange,
  open,
  legacyURL,
  getLegacyURL,
}) {
  const classVersion = CONTENT_CLASS_VERSIONS[contentType]

  const { data: fetchedClasses } = useQuery('kanban-classes', () =>
    getTeacherClasses({
      version: classVersion,
      includeDefault: ALLOWS_DEFAULT_ASSIGNMENT[contentType],
    })
  )

  const { addToast } = useToast()
  const [selectedClass, setSelectedClass] = useState(null)
  const [selectedSection, setSelectedSection] = useState(
    SYLLABUS_SECTIONS.prepping
  )
  const [isLoading, setIsLoading] = useState(false)
  const [duplicatedContent, setDuplicatedContent] = useState(null)

  const { id: contentId, title: contentTitle } = content

  const shouldCheckForDuplication = option =>
    option.version === CLASS_VERSION.kanban

  const checkForDuplicatedContent = async option => {
    const { classesIds } = await getClassesContainingContent(
      [CONTENT_ID_KEY[contentType]],
      contentId
    )

    return classesIds.includes(option.id)
  }

  const handleSelectClass = async option => {
    if (shouldCheckForDuplication(option)) {
      const isDuplicated = await checkForDuplicatedContent(option)
      if (isDuplicated) {
        return setDuplicatedContent(option)
      }
    }

    setDuplicatedContent(null)
    setSelectedClass(option)
  }

  const resetState = () => {
    setSelectedClass(null)
    setDuplicatedContent(null)
    setSelectedSection(SYLLABUS_SECTIONS.prepping)
  }

  const handleCancelDialog = () => {
    resetState()
    onCancel()
    onOpenChange(false)
  }

  const handleOpenChange = () => {
    resetState()
    onOpenChange(false)
  }

  const handleToast = data => {
    if (data.error) {
      return addToast({
        id: 'error-lesson-planner',
        content: 'Something went wrong. Please try again.',
        type: 'warning',
        timeout: 5000,
      })
    }

    return addToast({
      id: 'added-lesson-planner',
      content: 'Successfully added to class',
      type: 'success',
      timeout: 20000,
      action: (
        <TransparentButton
          as='a'
          ml={5}
          small
          color='white'
          href={`/teachers/classes/${data.classId}`}
        >
          <Icon icon='users-class' pr={2} />
          View Class
        </TransparentButton>
      ),
    })
  }

  const handleAddToClass = () => {
    setIsLoading(true)

    trackAddContentToClass({
      contentType,
      contentId,
      contentTitle,
      destinationColumn: selectedSection.id,
      destinationClassId: selectedClass.id,
    })

    const data = {
      yumi_class_id: selectedClass.id,
      teaching_state_cd: PLANNER_SECTIONS[selectedSection.id],
      [CONTENT_ID_KEY[contentType]]: contentId,
    }
    addLessonToClass({ data })
      .then(() => {
        resetState()
        setIsLoading(false)
        handleToast({ classId: selectedClass.id })
        onSave({ classId: selectedClass.id })
        onOpenChange(false)
      })
      .catch(error => {
        resetState()
        setIsLoading(false)
        handleToast({ error })
        onError()
      })
  }

  const onlyKanban = classVersion === CLASS_VERSION.kanban

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.StyledContent
          onPointerDownOutside={e => {
            e.preventDefault()
            resetState()
            onOpenChange(false)
          }}
        >
          <Dialog.StyledTitle>
            <Heading as='h6' mb={0}>
              Add to…
            </Heading>
          </Dialog.StyledTitle>
          <Box pt={3}>
            {duplicatedContent ? (
              <Box display='flex' alignItems='flex-start' mb={[2, null, 3]}>
                <Icon
                  icon='exclamation-triangle'
                  weight='solid'
                  color='yellow700'
                  fontSize={1}
                  pr={2}
                  pt={1}
                />
                <Text fontSize={0}>
                  <strong>{contentTitle}</strong> has already been added to{' '}
                  <strong>{duplicatedContent.name}</strong>. Please select
                  another&nbsp;class.
                </Text>
              </Box>
            ) : (
              <>
                <Text fontSize={0} mb={[2, null, 3]}>
                  Please select which class you would like to add{' '}
                  <strong>{contentTitle}</strong>&nbsp;to.
                </Text>
                {onlyKanban && (
                  <Label type='primary' compact>
                    <Box
                      display='flex'
                      justifyContent='center'
                      alignItems='center'
                      color='black'
                    >
                      <Icon icon='comment-alt-exclamation' pr={2} />
                      <Text>
                        Don&apos;t see your class on the list?{' '}
                        <TransparentButton
                          as='a'
                          fontWeight='600'
                          href='https://help.ellii.com/article/273-why-videos-cant-be-added-to-some-types-of-classes'
                          reset
                        >
                          <u>Learn&nbsp;more.</u>
                        </TransparentButton>
                      </Text>
                    </Box>
                  </Label>
                )}
                {COURSE_TYPE_WARNING[courseType] && (
                  <Label type='warning' compact mt={3}>
                    <Icon icon='exclamation-triangle' pr={2} />
                    {COURSE_TYPE_WARNING[courseType]}
                  </Label>
                )}
              </>
            )}
          </Box>
          {/* SELECT CLASS */}
          {fetchedClasses && (
            <Box mt={4} mb={2}>
              <Select
                name='class'
                label='Select a class'
                icon='users-class'
                options={fetchedClasses}
                getOptionText={c => c.name}
                getOptionValue={c => c.id}
                value='value'
                onChange={option => handleSelectClass(option)}
                placeholder='Please select…'
              />
            </Box>
          )}

          {!duplicatedContent && selectedClass && (
            <>
              {/* SELECT SYLLABUS SECTION */}
              {selectedClass?.version === CLASS_VERSION.kanban && (
                <Box opacity={!selectedClass ? 0.4 : 1}>
                  <Select
                    name='class'
                    label='Choose location'
                    icon='columns'
                    options={SYLLABUS_SECTIONS_LIST}
                    getOptionText={section => section.content}
                    value={selectedSection}
                    getOptionValue={section => section.id}
                    onChange={option => setSelectedSection(option)}
                    disabled={!selectedClass}
                  />
                </Box>
              )}
              {onlyKanban ? (
                <Box textAlign='center' mb={3} mt={4}>
                  <Button
                    adaptive
                    secondary
                    mr={[1, 2]}
                    onClick={handleCancelDialog}
                  >
                    <Icon icon='times' pr={2} />
                    Cancel
                  </Button>
                  <Button
                    adaptive
                    onClick={handleAddToClass}
                    disabled={!selectedClass}
                  >
                    <Icon icon='check' pr={2} />
                    Save
                  </Button>
                </Box>
              ) : (
                <>
                  <Box textAlign='center' mt={4}>
                    <Button
                      adaptive
                      secondary
                      mr={[1, 2]}
                      onClick={handleCancelDialog}
                    >
                      <Icon icon='times' pr={2} />
                      Cancel
                    </Button>
                    <Button
                      as='a'
                      adaptive
                      href={legacyURL || getLegacyURL(selectedClass)}
                      display={
                        selectedClass?.version === CLASS_VERSION.kanban &&
                        'none'
                      }
                    >
                      Next
                      <Icon icon='arrow-right' pl={2} />
                    </Button>

                    <Button
                      disabled={isLoading}
                      mr={1}
                      onClick={handleAddToClass}
                      display={
                        selectedClass?.version === CLASS_VERSION.legacy &&
                        'none'
                      }
                      adaptive
                    >
                      {isLoading ? (
                        <LoadingSpinner mr={2} />
                      ) : (
                        <Icon pr={2} icon='check' />
                      )}
                      Save
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}
        </Dialog.StyledContent>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

AddToClassDialog.defaultProps = {
  legacyURL: null,
  courseType: null,
  getLegacyURL: () => {},
  onError: () => {},
  onCancel: () => {},
  onSave: () => {},
}

AddToClassDialog.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  contentType: PropTypes.oneOf([
    CONTENT_TYPES.lesson,
    CONTENT_TYPES.course,
    CONTENT_TYPES.flashcardSet,
    CONTENT_TYPES.flashcardCategory,
    CONTENT_TYPES.video,
  ]).isRequired,
  courseType: PropTypes.oneOf(Object.keys(COURSE_TYPES)),
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  onError: PropTypes.func,
  legacyURL: PropTypes.string,
  getLegacyURL: PropTypes.func,
}
